import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Mail, Phone, Check, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Newsletter = () => {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    preferences: ['promotions', 'nouveautes']
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value.trim()) return 'L\'email est requis';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Email invalide';
        return '';
      case 'phone':
        if (value && !/^(0[1-9])[\s.-]?([0-9]{2}[\s.-]?){4}$/.test(value.replace(/\s/g, ''))) {
          return 'Numéro de téléphone invalide';
        }
        return '';
      default:
        return '';
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const togglePreference = (pref) => {
    setFormData(prev => ({
      ...prev,
      preferences: prev.preferences.includes(pref)
        ? prev.preferences.filter(p => p !== pref)
        : [...prev.preferences, pref]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const emailError = validateField('email', formData.email);
    const phoneError = validateField('phone', formData.phone);
    
    if (emailError || phoneError) {
      setErrors({ email: emailError, phone: phoneError });
      setTouched({ email: true, phone: true });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          phone: formData.phone || null,
          preferences: formData.preferences
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'inscription');
      }
      
      setIsSubscribed(true);
      toast.success('🎉 Bienvenue ! Vous êtes inscrit à notre newsletter');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'inscription');
    } finally {
      setIsSubmitting(false);
    }
  };

  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-1 mt-1 text-red-400 text-sm">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  };

  if (isSubscribed) {
    return (
      <section className="py-16 bg-gradient-to-br from-[#0A2320] to-[#153254] font-['Poppins']">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-3xl font-bold text-white mb-4">Merci de votre inscription !</h3>
            <p className="text-white/80 text-lg">
              Vous recevrez très bientôt nos meilleures offres et actualités.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-[#0A2320] to-[#153254] font-['Poppins']">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Texte */}
            <div className="text-[#F8FAF9]">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-[#F8FAF9]">Restez informé !</h3>
              
              <p className="text-[#F8FAF9]/80 text-lg mb-6">
                Inscrivez-vous à notre newsletter pour recevoir nos offres exclusives, 
                conseils d'entretien et actualités.
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-white/90">Offres et promotions exclusives</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-white/90">Conseils d'entretien maison & jardin</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-white/90">Actualités et jeux concours</span>
                </li>
              </ul>
            </div>
            
            {/* Formulaire */}
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-white/20">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      placeholder="votre@email.fr"
                      className={`pl-10 rounded-xl bg-white/90 border-0 h-12 ${errors.email && touched.email ? 'ring-2 ring-red-500' : ''}`}
                      data-testid="newsletter-email-input"
                    />
                  </div>
                  {touched.email && <ErrorMessage error={errors.email} />}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Téléphone (optionnel)</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      onBlur={() => handleBlur('phone')}
                      placeholder="06 12 34 56 78"
                      className={`pl-10 rounded-xl bg-white/90 border-0 h-12 ${errors.phone && touched.phone ? 'ring-2 ring-red-500' : ''}`}
                      data-testid="newsletter-phone-input"
                    />
                  </div>
                  {touched.phone && <ErrorMessage error={errors.phone} />}
                  <p className="text-xs text-white/60 mt-1">Pour recevoir nos offres par SMS</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-3">Je souhaite recevoir :</label>
                  <div className="space-y-2">
                    {[
                      { id: 'promotions', label: 'Offres et promotions' },
                      { id: 'nouveautes', label: 'Nouveautés et services' },
                      { id: 'conseils', label: 'Conseils et astuces' }
                    ].map(pref => (
                      <div key={pref.id} className="flex items-center gap-3">
                        <Checkbox
                          id={pref.id}
                          checked={formData.preferences.includes(pref.id)}
                          onCheckedChange={() => togglePreference(pref.id)}
                          className="border-white/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                        />
                        <label htmlFor={pref.id} className="text-sm text-white/90 cursor-pointer">
                          {pref.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#D4AF37] hover:bg-[#c9a030] text-white font-bold h-12 rounded-xl shadow-lg"
                  data-testid="newsletter-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Inscription...
                    </>
                  ) : (
                    "S'inscrire à la newsletter"
                  )}
                </Button>
                
                <p className="text-xs text-white/50 text-center">
                  En vous inscrivant, vous acceptez de recevoir nos communications. 
                  Désinscription possible à tout moment.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
